/* =========================================================================
   CcWithdrawActionDialog.tsx – approve (send) or reject (refund) one request

   Approving asks for the network as well as the 2FA code: the player types the
   chain as free text on the withdrawal form ("Ethereum", "trc20", …), which
   does not always match CCPayment's chain code — and sending to the wrong
   chain loses the coins. So the admin confirms it against the real list.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { apiFetch } from '../../utils/api';
import { CcNetwork, CcWithdrawRow, inputSx } from './types';

interface Props {
  action: { kind: 'approve' | 'reject'; row: CcWithdrawRow } | null;
  onClose: () => void;
  /** Called after a successful approve/reject so the list can refresh. */
  onDone: () => void;
}

export default function CcWithdrawActionDialog({ action, onClose, onDone }: Props) {
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [chain, setChain] = useState('');
  const [networks, setNetworks] = useState<CcNetwork[]>([]);
  const [netLoad, setNetLoad] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const row = action?.row;

  const loadNetworks = useCallback(async () => {
    if (!row) return;
    setNetLoad(true);
    setErr('');
    try {
      const qs = new URLSearchParams({ coin: row.coin, chain: row.chain || '' });
      const res = await apiFetch<{ networks: CcNetwork[]; matched: CcNetwork | null }>(
        `/api/ccpayment/withdraw/networks?${qs.toString()}`
      );
      setNetworks(res.networks || []);
      // Pre-select whatever the user's free text resolved to, if anything.
      setChain(res.matched?.chain || '');
    } catch (e: any) {
      setErr(e?.message || 'Could not load networks for this coin');
      setNetworks([]);
    } finally {
      setNetLoad(false);
    }
  }, [row]);

  useEffect(() => {
    setCode(''); setReason(''); setChain(''); setNetworks([]); setErr('');
    if (action?.kind === 'approve') loadNetworks();
  }, [action, loadNetworks]);

  const submit = async () => {
    if (!action) return;
    if (!code.trim()) { setErr('Enter your 2FA code'); return; }
    if (action.kind === 'approve' && !chain) { setErr('Select the network to send on'); return; }

    setBusy(true);
    setErr('');
    try {
      const path = action.kind === 'approve'
        ? '/api/ccpayment/withdraw/approve'
        : '/api/ccpayment/withdraw/reject';
      const body = action.kind === 'approve'
        ? { id: action.row.id, code: code.trim(), chain }
        : { id: action.row.id, code: code.trim(), reason: reason.trim() || 'Rejected by admin' };

      await apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
      onDone();
      onClose();
    } catch (e: any) {
      setErr(e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const approving = action?.kind === 'approve';

  return (
    <Dialog open={!!action} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ bgcolor: '#0E1831', color: '#F9F9F9', fontSize: '1rem' }}>
        {approving ? 'Approve & send' : 'Reject & refund'}
        {row && ` · #${row.id}`}
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#0E1831', pt: 2 }}>
        {row && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#F9F9F9', fontSize: '0.9rem', fontWeight: 700 }}>
              {Number(row.amount)} {row.coin?.toUpperCase()}
            </Typography>
            <Typography sx={{ color: '#8384A5', fontSize: '0.7rem' }}>
              User #{row.uid}{row.username ? ` · ${row.username}` : ''}
            </Typography>
            <Typography sx={{ color: '#F9F9F9', fontSize: '0.72rem', wordBreak: 'break-all', mt: 0.5 }}>
              {row.wallet}
            </Typography>
            <Typography sx={{ color: '#8384A5', fontSize: '0.68rem', mt: 0.5 }}>
              Chain entered by user: {row.chain || '—'}
            </Typography>
          </Box>
        )}

        {approving && (
          <>
            <TextField
              select fullWidth size="small" label="Network to send on"
              value={chain} onChange={(e) => setChain(e.target.value)}
              disabled={netLoad} sx={{ ...inputSx, mb: 2 }}
              helperText={netLoad ? 'Loading networks…' : 'Must match the address — a wrong chain loses the coins'}
              FormHelperTextProps={{ sx: { color: '#8384A5', fontSize: '0.65rem' } }}
            >
              {networks.map((n) => (
                <MenuItem key={n.chain} value={n.chain}>
                  {n.chain} — {n.chainFullName}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        {!approving && (
          <TextField
            fullWidth size="small" label="Reason (optional)"
            value={reason} onChange={(e) => setReason(e.target.value)}
            sx={{ ...inputSx, mb: 2 }}
          />
        )}

        <TextField
          autoFocus fullWidth size="small" label="Your 2FA code" placeholder="000000"
          value={code} onChange={(e) => setCode(e.target.value)}
          inputProps={{ maxLength: 6 }} sx={inputSx}
        />
        <Typography sx={{ color: '#8384A5', fontSize: '0.65rem', mt: 1 }}>
          {approving
            ? 'The 6-digit code from your authenticator app. Each code works once; if it fails the request stays pending and you can retry.'
            : "The amount is credited back to the user's balance."}
        </Typography>

        {err && (
          <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(224,27,79,0.1)', color: '#E01B4F', fontSize: '0.75rem' }}>
            {err}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#0E1831', px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: '#8384A5' }}>Cancel</Button>
        <Button
          onClick={submit} disabled={busy} variant="contained"
          sx={{
            bgcolor: approving ? '#0ECC68' : '#E01B4F',
            '&:hover': { bgcolor: approving ? '#0BB35C' : '#C41843' },
          }}
        >
          {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : approving ? 'Approve & Send' : 'Reject & Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
